import { useState, useRef, useEffect } from "react";
import {
    Box,
    Heading,
    VStack,
    HStack,
    Text,
    Button,
    Grid,
    Icon,
} from "@chakra-ui/react";
import { FaPython, FaJs, FaReact, FaCheckCircle, FaHtml5, FaCss3Alt, FaTags } from "react-icons/fa";
import { useColorModeValue } from "../ui/color-mode";
import { useAccessibility } from "../../context/AccessibilityContext";
import navigationSound from "../../assets/sounds/navigation.mp3";

export const CourseSelectionScreen = ({ onNext, onBack, onViewPricing }) => {
    const [selectedCourse, setSelectedCourse] = useState(null);
    const [focusedIndex, setFocusedIndex] = useState(0);
    const { soundEnabled } = useAccessibility();

    const pythonRef = useRef(null);
    const jsRef = useRef(null);
    const reactRef = useRef(null);
    const htmlRef = useRef(null);
    const cssRef = useRef(null);
    const containerRef = useRef(null);

    const cardBg = useColorModeValue("white", "gray.800");
    const headingColor = useColorModeValue("gray.700", "white");
    const textColor = useColorModeValue("gray.600", "gray.400");
    const borderColor = useColorModeValue("gray.200", "gray.600");
    const hoverBg = useColorModeValue("blue.50", "blue.900");
    const selectedBg = useColorModeValue("blue.100", "blue.800");
    const screenBg = useColorModeValue("gray.50", "gray.900");

    useEffect(() => {
        if (containerRef.current) {
            containerRef.current.focus();
        }
    }, []);

    const playSound = (sound) => {
        if (soundEnabled) {
            new Audio(sound).play();
        }
    };

    const courses = [
        {
            id: "html",
            title: "HTML & Accessibility",
            description: "Learn HTML with a focus on semantic structure and screen reader support.",
            icon: FaHtml5,
            features: [
                "10 accessibility-focused modules",
                "Learn how screen readers parse HTML",
                "Semantic markup & ARIA roles",
                "Build screen-reader-friendly pages"
            ],
            ref: htmlRef,
            ariaLabel: "HTML and Accessibility Fundamentals. Learn semantic HTML for screen readers.",
            badge: "Beginner"
        },
        {
            id: "css",
            title: "CSS for Accessible Design",
            description: "CSS with a focus on high contrast, focus indicators, and low-vision support.",
            icon: FaCss3Alt,
            features: [
                "10 modules on accessible styling",
                "Color contrast & readability",
                "Focus styles & keyboard visibility",
                "Reduced motion support"
            ],
            ref: cssRef,
            ariaLabel: "CSS for Accessible Design. Learn to create visually accessible websites.",
            badge: "Beginner"
        },
        {
            id: "python",
            title: "Python Masterclass",
            description: "Learn Python from scratch. Ideal for beginners.",
            icon: FaPython,
            features: [
                "10 interactive modules",
                "Learn basics to advanced",
                "Real-world coding challenges",
                "Complete accessibility support"
            ],
            ref: pythonRef,
            ariaLabel: "Python Masterclass. Navigate through and learn Python."
        },
        {
            id: "javascript",
            title: "JavaScript Fundamentals",
            description: "Master JavaScript in 10 sections.",
            icon: FaJs,
            features: [
                "10 sections of JS concepts",
                "DOM manipulation basics",
                "Asynchronous JS",
                "Interactive learning"
            ],
            ref: jsRef,
            ariaLabel: "JavaScript Fundamentals. Learn JS step by step."
        },
        {
            id: "react",
            title: "React 10 Days Learning",
            description: "Learn React step by step in 10 days.",
            icon: FaReact,
            features: [
                "10 days of continuous learning",
                "Component-based architecture",
                "State management & Hooks",
                "Build real-time apps"
            ],
            ref: reactRef,
            ariaLabel: "React 10 Days Learning. Master React in 10 days."
        }
    ];

    const handleKeyDown = (e) => {
        let maxIndex = courses.length - 1;
        if (e.key === "ArrowRight" || e.key === "ArrowDown") {
            e.preventDefault();
            const nextIndex = focusedIndex === maxIndex ? 0 : focusedIndex + 1;
            setFocusedIndex(nextIndex);
            setSelectedCourse(courses[nextIndex].id);
            courses[nextIndex].ref.current?.focus();
            playSound(navigationSound);
        } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
            e.preventDefault();
            const prevIndex = focusedIndex === 0 ? maxIndex : focusedIndex - 1;
            setFocusedIndex(prevIndex);
            setSelectedCourse(courses[prevIndex].id);
            courses[prevIndex].ref.current?.focus();
            playSound(navigationSound);
        } else if (e.key === "Enter" && selectedCourse) {
            e.preventDefault();
            handleContinue();
        }
    };

    const handleCardClick = (typeId, index) => {
        setSelectedCourse(typeId);
        setFocusedIndex(index);
        playSound(navigationSound);
    };

    const handleContinue = () => {
        if (selectedCourse) {
            playSound(navigationSound);
            onNext(selectedCourse);
        }
    };

    return (
        <Box
            p={{ base: 4, md: 8 }}
            bg={screenBg}
            minHeight="100vh"
            width="100%"
        >
            <VStack spacing={8} maxW="1200px" mx="auto">
                {/* Header */}
                <Box textAlign="center" width="100%">
                    <Heading
                        size="2xl"
                        color={headingColor}
                        mb={4}
                        id="course-selection-heading"
                    >
                        Choose Your Course
                    </Heading>
                    <Text fontSize="lg" color={textColor} maxW="800px" mx="auto">
                        Select which programming language or framework you want to learn.
                        Use arrow keys to navigate between options and Enter to select.
                    </Text>
                </Box>

                {/* Course Selection Cards */}
                <Grid
                        templateColumns={{ base: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(3, 1fr)", xl: "repeat(5, 1fr)" }}
                    gap={6}
                    width="100%"
                    ref={containerRef}
                    tabIndex={0}
                    onKeyDown={handleKeyDown}
                    role="radiogroup"
                    aria-labelledby="course-selection-heading"
                    aria-activedescendant={selectedCourse}
                >
                    {courses.map((course, index) => (
                        <Box
                            key={course.id}
                            ref={course.ref}
                            onClick={() => handleCardClick(course.id, index)}
                            onFocus={() => {
                                setSelectedCourse(course.id);
                                setFocusedIndex(index);
                            }}
                            tabIndex={0}
                            role="radio"
                            aria-checked={selectedCourse === course.id}
                            aria-label={course.ariaLabel}
                            p={8}
                            bg={selectedCourse === course.id ? selectedBg : cardBg}
                            borderRadius="xl"
                            borderWidth="3px"
                            borderColor={selectedCourse === course.id ? "blue.500" : borderColor}
                            boxShadow={selectedCourse === course.id ? "xl" : "md"}
                            cursor="pointer"
                            transition="all 0.3s ease"
                            _hover={{
                                bg: selectedCourse === course.id ? selectedBg : hoverBg,
                                transform: "translateY(-4px)",
                                boxShadow: "xl",
                            }}
                            _focus={{
                                outline: "none",
                                borderColor: "blue.500",
                                boxShadow: "0 0 0 3px rgba(66, 153, 225, 0.5)",
                            }}
                        >
                            <VStack spacing={6} align="stretch">
                                <HStack spacing={4}>
                                    <Icon
                                        as={course.icon}
                                        boxSize={10}
                                        color="blue.500"
                                        aria-hidden="true"
                                    />
                                    <Heading size="md" color={headingColor}>
                                        {course.title}
                                    </Heading>
                                    {selectedCourse === course.id && (
                                        <Icon
                                            as={FaCheckCircle}
                                            boxSize={6}
                                            color="green.500"
                                            aria-label="Selected"
                                        />
                                    )}
                                </HStack>

                                <Text color={textColor} fontSize="md">
                                    {course.description}
                                </Text>

                                <VStack align="stretch" spacing={2}>
                                    <Text fontWeight="bold" color={headingColor} fontSize="sm">
                                        Features:
                                    </Text>
                                    {course.features.map((feature, idx) => (
                                        <HStack key={idx} spacing={2}>
                                            <Icon as={FaCheckCircle} color="green.500" boxSize={4} />
                                            <Text fontSize="sm" color={textColor}>
                                                {feature}
                                            </Text>
                                        </HStack>
                                    ))}
                                </VStack>
                            </VStack>
                        </Box>
                    ))}
                </Grid>

                <HStack spacing={4} justify="center" width="100%" flexWrap="wrap">
                    <Button
                        onClick={onBack}
                        variant="outline"
                        size="lg"
                        aria-label="Go back to previous screen"
                    >
                        Go Back
                    </Button>
                    <Button
                        onClick={handleContinue}
                        colorScheme="blue"
                        size="lg"
                        isDisabled={!selectedCourse}
                        aria-label="Continue with selected course"
                    >
                        Start Selected Course
                    </Button>
                    {onViewPricing && (
                        <Button
                            onClick={onViewPricing}
                            colorScheme="purple"
                            variant="outline"
                            size="lg"
                            leftIcon={<Icon as={FaTags} aria-hidden="true" />}
                            aria-label="View subscription plans and pricing"
                        >
                            View Plans & Pricing
                        </Button>
                    )}
                </HStack>

                <Box className="sr-only" role="region" aria-live="polite" aria-atomic="true">
                    {selectedCourse && (
                        <Text>
                            {courses.find(c => c.id === selectedCourse)?.title} selected. Press Enter to continue.
                        </Text>
                    )}
                </Box>
            </VStack>
        </Box>
    );
};
